import React, {useEffect, useRef} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {AuthContainer} from './Auth.styled';
import TAILogo from '../../assets/images/taia_logo.png';
import LogoWithLanguageSwitcher from '../../components/common/LogoWithLanguageSwitcher';
import {useSnackbar} from '../../contexts/SnackbarContext';
import {usePageTitle} from '../../hooks/usePageTitle';
import {PageTitleKeys} from '../../config/routeConstants';
import {Box, CircularProgress} from '@mui/material';
import styled from 'styled-components';
import axios from "axios";
import {parseError} from "../../services/http.service";
import {useTranslation} from 'react-i18next';

const Spinner = styled(CircularProgress)`
    color: #007db8;
`;

const AccountActivation: React.FC = () => {
    usePageTitle(PageTitleKeys.AccountActivation);
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {showSnackbar} = useSnackbar();
    const activationAttempted = useRef(false);

    useEffect(() => {
        if (activationAttempted.current) {
            return;
        }
        activationAttempted.current = true;

        const token = searchParams.get('token');

        if (!token) {
            navigate('/login', {replace: true});
            return;
        }

        const activateAccount = async () => {
            try {
                await axios.get('/auth/activate', {
                    params: {activationToken: token}
                });

                showSnackbar(t('accountActivation.successMessage'), 'success');
                navigate("/email-confirmed");
            } catch (error) {
                showSnackbar(parseError(error), "error");
                navigate('/login', {replace: true});
            }
        };

        activateAccount();
    }, [searchParams, navigate, showSnackbar, t]);

    return (
        <AuthContainer role="region" aria-labelledby="account-activation-title">
            <LogoWithLanguageSwitcher src={TAILogo} alt={t('auth.logo.alt')} clickable={true} />
            <h2 id="account-activation-title" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                {t('accountActivation.title')}
            </h2>
            <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                height="300px" 
                marginTop="24px"
                aria-live="polite"
                aria-atomic={true}
            >
                <Spinner size={120} thickness={4} aria-hidden={true}/>
                <Box role="status" aria-live="polite" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                    {t('auth.loading')}
                </Box>
            </Box>
        </AuthContainer>
    );
};

export default AccountActivation;
